import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { BasicDataChartResponse } from '../config/chart-data';
import type { RecordsAnalysisResultsDto, WidgetInsightsResponseDto } from '../config/widgetApi';
import type { RootState } from '../store';

export const JobStatus = {
  NotStarted: 0,
  InProgress: 1,
  Success: 2,
  Failed: 3,
} as const;
export type JobStatus = (typeof JobStatus)[keyof typeof JobStatus];

interface JobDetails {
  status?: JobStatus;
  completedDate?: string;
  startedDate?: string;
  id?: string;
  error?: string;
  result?: WidgetInsightsResponseDto | RecordsAnalysisResultsDto;
  extraPrompt?: string;
}

export interface SidePanelItem {
  key: string;
  type: 'AI_INSIGHT' | 'RECORD_INSIGHT';
  widgetId: string;
  title: string;
  icon: string;
  job?: JobDetails;
  chartData?: BasicDataChartResponse[];
}

export interface SidePanelState {
  isOpen: boolean;
  isFullScreen: boolean;
  items: SidePanelItem[];
  activeItemKey?: string;
}

const initialState: SidePanelState = {
  isOpen: false,
  isFullScreen: false,
  items: [],
  activeItemKey: undefined,
};

const sidePanelSlice = createSlice({
  name: 'sidePanel',
  initialState: initialState,
  reducers: {
    setSidePanelOpen(state, action: PayloadAction<boolean>) {
      state.isOpen = action.payload;

      // If closing the side panel, also reset its state
      if (action.payload === false) {
        Object.assign(state, initialState);
      }
    },
    setSidePanelFullScreen(state, action: PayloadAction<boolean>) {
      state.isFullScreen = action.payload;
    },
    deleteSidePanelItem(state, action: PayloadAction<string>) {
      const deletedItemIndex = state.items.findIndex((item) => item.key === action.payload);

      state.items = state.items.filter((item) => item.key !== action.payload);

      // If no items left, close the side panel
      if (state.items.length === 0) {
        Object.assign(state, initialState);
        return;
      }

      // If the deleted item was the active one, set active to previous item or next item or undefined
      if (state.activeItemKey === action.payload && deletedItemIndex !== -1) {
        const previousItem = deletedItemIndex > 0 ? state.items[deletedItemIndex - 1] : undefined;
        const nextItem = deletedItemIndex < state.items.length ? state.items[deletedItemIndex] : undefined;
        state.activeItemKey = previousItem?.key || nextItem?.key || undefined;
      }
    },
    setActiveSidePanelItemKey(state, action: PayloadAction<string | undefined>) {
      state.activeItemKey = action.payload;
    },
    addSidePanelItem(state, action: PayloadAction<SidePanelItem>) {
      // If panel is closed, open it
      if (!state.isOpen) {
        state.isOpen = true;
      }

      // Check if item already exists by key
      state.activeItemKey = action.payload.key;
      const existingItemIndex = state.items.findIndex((item) => item.key === action.payload.key);
      if (existingItemIndex !== -1) {
        return;
      } else {
        state.items.push(action.payload);
      }
    },
    setSidePanelItemJob(state, action: PayloadAction<{ key: string; job: JobDetails }>) {
      const item = state.items.find((item) => item.key === action.payload.key);
      if (item) {
        item.job = { ...item.job, ...action.payload.job };
      }
    },
    removeSidePanelItemJob(state, action: PayloadAction<{ key: string }>) {
      const item = state.items.find((item) => item.key === action.payload.key);
      if (item) {
        delete item.job;
      }
    },
    restoreSidePanelState(state, action: PayloadAction<SidePanelState>) {
      const { isOpen, isFullScreen, items, activeItemKey } = action.payload;

      state.isOpen = isOpen;
      state.isFullScreen = isFullScreen;
      state.items = items;
      state.activeItemKey = activeItemKey;
    },
  },
});

export const {
  setSidePanelOpen,
  setSidePanelFullScreen,
  deleteSidePanelItem,
  setActiveSidePanelItemKey,
  addSidePanelItem,
  setSidePanelItemJob,
  removeSidePanelItemJob,
  restoreSidePanelState,
} = sidePanelSlice.actions;

export const selectSidePanelOpen = (state: RootState) => state.rootReducer.sidePanel.isOpen;
export const selectSidePanelFullScreen = (state: RootState) => state.rootReducer.sidePanel.isFullScreen;
export const selectSidePanelItems = (state: RootState) => state.rootReducer.sidePanel.items;
export const selectActiveSidePanelItemKey = (state: RootState) => state.rootReducer.sidePanel.activeItemKey;

export const selectActiveSidePanelItem = createSelector(
  [selectSidePanelItems, selectActiveSidePanelItemKey],
  (items, activeKey) => items.find((item) => item.key === activeKey)
);

export default sidePanelSlice.reducer;
